import * as React from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

import LocationsModal from "../modals/locationsModal";

import { DriveRowSummary } from "./driveRowSummary";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { Item } from "@/types/fse";
import { DriveRowButtons } from "@/views/drive/components/driveRowButtons";
import { ItemTxProgress } from "@/views/drive/types";
import * as analytics from "@/services/analytics";
import { DriveRowName } from "@/views/drive/components/driverRowName";
import { formatFolderContentsCount } from "@/views/drive/components/driverRowName/folderCounts";
import { DriveRowMoving } from "@/views/drive/components/driveRowMoving";
import clsx from "clsx";
import { cn } from "@/lib/utils";
import { GripVertical, ListCollapse, MoreHorizontal } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import { ShiftCheckbox } from "@/components/ui/shift-checkbox";
import { ShareModal } from "@/views/drive/modals/shareModal";
import { DriveOwnerCell } from "@/views/drive/workspace/DriveOwnerCell";
import { DriveSharingSummary } from "@/views/drive/workspace/DriveSharingSummary";
import type { DirectoryUsersById } from "@/views/drive/workspace/types";
import { getDriveAccessLabel } from "@/views/drive/workspace/utils";
import { DriveRowMenu } from "@/views/drive/components/driveRowMenu";
import { getDriveItemProcessingState } from "./driveItemProcessingState";
import { SPLIT_GRID_COLUMNS } from "@/views/drive/workspace/constants";
import type { ClassStyle } from "@/views/drive/documentMap/types";

interface Props {
  item: Item;
  usersById: DirectoryUsersById;
  authenticatedUserId: string;
  progressForItem: Record<string, ItemTxProgress>;
  hiddenButtons?: Array<string>;
  isMoving?: boolean;
  isSelected?: boolean;
  currentFolderId: string | null;
  onItemSelection?: (itemId: string, isCtrlOrCmd: boolean, shiftKey?: boolean, targetChecked?: boolean) => void;
  onToggleSelect?: (itemId: string, checked: boolean, shiftKey: boolean) => void;
  isMutable?: boolean;
  /** Split view's Role column. Absent: the row shows Owner, Access and Created. */
  splitRole?: ClassStyle | null;
}

export const DriveRow: React.FC<Props> = ({
  item,
  usersById,
  authenticatedUserId,
  progressForItem,
  currentFolderId,
  hiddenButtons = [],
  isMoving = false,
  isSelected = false,
  onItemSelection,
  onToggleSelect,
  isMutable = true,
  splitRole,
}) => {
  const split = splitRole !== undefined;
  const { isActionable, isRejected } = getDriveItemProcessingState(item, progressForItem[item.id]);
  const [expanded, setExpanded] = React.useState(false);
  const [isDragDisabled, setIsDragDisabled] = React.useState(false);
  const [isShareOpen, setIsShareOpen] = React.useState(false);
  const { formatDate } = useDateFormatter();
  const folderContentsCount = item.type === "folder" ? formatFolderContentsCount(item) : null;

  // dnd-kit hooks
  const {
    attributes: draggableAttributes,
    listeners: draggableListeners,
    setNodeRef: setDraggableRef,
    transform: dragTransform,
    isDragging,
  } = useDraggable({
    id: item.id,
    data: {
      item,
      isSelected,
    },
    disabled: !isMutable || isDragDisabled || isRejected,
  });

  const { isOver: isDropOver, setNodeRef: setDroppableRef } = useDroppable({
    id: `droppable-${item.id}`,
    data: {
      item,
    },
    disabled: !isMutable || item.type !== "folder" || isDragging,
  });

  const toggleExpanded = () => {
    if (!expanded) {
      analytics.showSummary(item.name);
    }

    setExpanded(prev => !prev);
  };

  const dragStyle = {
    transform: CSS.Translate.toString(dragTransform),
    opacity: isDragging ? 0.5 : 1,
  };

  const combinedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setDraggableRef(node);
      if (item.type === "folder") {
        setDroppableRef(node);
      }
    },
    [item.type, setDraggableRef, setDroppableRef],
  );

  const handleRowClick = (e: React.MouseEvent) => {
    if (!isMutable) return;
    if (e.target !== e.currentTarget && !(e.target as Element).closest(".row-content")) {
      return;
    }

    if (onItemSelection) {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      const isShiftKey = e.shiftKey;

      if (!isCtrlOrCmd && !isShiftKey) {
        onItemSelection(item.id, false);
      } else {
        onItemSelection(item.id, isCtrlOrCmd, isShiftKey);
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isMutable) return;
    if (onItemSelection) {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      const isShiftKey = e.shiftKey;

      if (isCtrlOrCmd || isShiftKey) {
        setIsDragDisabled(true);
        onItemSelection(item.id, isCtrlOrCmd, isShiftKey);

        e.preventDefault();
        e.stopPropagation();
      } else {
        setIsDragDisabled(false);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragDisabled(false);
  };

  return (
    <>
      {isShareOpen && <ShareModal setIsOpen={setIsShareOpen} isOpen={isShareOpen} item={item} />}
      <div
        ref={combinedRef}
        className={clsx(
          "relative grid items-center gap-3 px-4 py-3 transition-colors hover:bg-ivory-mist-200",
          split
            ? SPLIT_GRID_COLUMNS
            : "grid-cols-[2rem_minmax(0,1fr)_2rem] md:grid-cols-[2rem_minmax(16rem,1.5fr)_8rem_6rem_2rem] xl:grid-cols-[2rem_minmax(16rem,1fr)_9rem_7rem_7rem_minmax(34rem,auto)_2rem]",
          {
            "border-2 border-dashed border-blue-300 bg-blue-50": item.type === "folder" && isDropOver,
            "opacity-50": isDragging,
            "animate-pulse border-l-4 border-yellow-400 bg-yellow-50": isMoving,
            "border-l-4 border-blue-500 bg-blue-100": isSelected,
            "border-l-4 border-red-300 bg-red-50/50 opacity-75": isRejected && !isSelected,
          },
        )}
        style={dragStyle}
        data-testid="drive-row"
        role="row"
      >
        {onToggleSelect && isMutable && (
          <div role="cell" className="flex items-center">
            <ShiftCheckbox
              checked={isSelected}
              onCheckedChange={(checked, meta) => onToggleSelect(item.id, checked, meta.shiftKey)}
              onClick={e => e.stopPropagation()}
              className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
              data-testid={`multi-select-checkbox-${item.id}`}
            />
          </div>
        )}
        {(!onToggleSelect || !isMutable) && <div role="cell" />}
        <div role="cell" className="row-content relative min-w-0">
          <div
            className={clsx("group flex items-center", {
              "cursor-pointer": !isRejected,
            })}
            onClick={handleRowClick}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
          >
            {!isRejected && isMutable ? (
              <Tooltip
                text="Drag to move"
                className="rounded bg-black px-2 py-1 text-xs text-white"
                delayDuration={200}
              >
                <span
                  className="mr-2 flex h-6 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-gray-400 transition-colors active:cursor-grabbing group-hover:bg-gray-100 group-hover:text-gray-700"
                  aria-label="Drag to move"
                  {...(!isDragDisabled ? draggableAttributes : {})}
                  {...(!isDragDisabled ? draggableListeners : {})}
                >
                  <GripVertical className="h-4 w-4" />
                </span>
              </Tooltip>
            ) : (
              <span className="mr-2 h-6 w-5 shrink-0" aria-hidden="true" />
            )}
            <DriveRowName item={item} isDragging={isDragging} />
            {item.type === "folder" && isDropOver && (
              <span className="ml-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                Drop here
              </span>
            )}
            {isMoving && <DriveRowMoving />}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
            {folderContentsCount && <span title="Includes items in subfolders">{folderContentsCount}</span>}
            <DriveSharingSummary
              item={item}
              authenticatedUserId={authenticatedUserId}
              usersById={usersById}
              onOpenShare={() => setIsShareOpen(true)}
            />
            <LocationsModal itemName={item.name} locations={item.locations} />
            {item.type === "file" && isActionable && (
              <Button onClick={toggleExpanded} variant="light-beige-secondary" size="xs" className="p-1 text-xxs">
                <ListCollapse className="mr-1 h-3 w-3" />
                {expanded ? "Hide Summary" : "Show Summary"}
              </Button>
            )}
          </div>
          {!split && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 md:hidden">
              <DriveOwnerCell ownerId={item.owner_id} usersById={usersById} />
              <span>{getDriveAccessLabel(item, authenticatedUserId)}</span>
              <span title={item.created_at}>{formatDate(item.created_at)}</span>
            </div>
          )}
        </div>
        {split ? (
          <div role="cell" className="min-w-0">
            {splitRole && (
              <span
                className={cn("inline-flex rounded px-1.5 py-0.5 text-xs font-medium", splitRole.fill, splitRole.text)}
              >
                {splitRole.label}
              </span>
            )}
          </div>
        ) : (
          <>
            <div role="cell" className="hidden min-w-0 md:block">
              <DriveOwnerCell ownerId={item.owner_id} usersById={usersById} />
            </div>
            <div role="cell" className="hidden md:block">
              <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                {getDriveAccessLabel(item, authenticatedUserId)}
              </span>
            </div>
            <div role="cell" className="hidden text-sm text-gray-600 xl:block" title={item.created_at}>
              {formatDate(item.created_at)}
            </div>
          </>
        )}
        <div
          role="cell"
          className={cn(
            "col-start-2 row-start-2 min-w-0",
            split ? "col-span-2" : "md:col-span-3 md:col-start-2 xl:col-span-1 xl:col-start-6 xl:row-start-1",
          )}
        >
          <DriveRowButtons
            variant="compact"
            currentFolderId={currentFolderId}
            item={item}
            progressForItem={progressForItem}
            hiddenButtons={[...hiddenButtons, "options"]}
          />
        </div>
        <div
          role="cell"
          className={cn("row-start-1", split ? "col-start-4" : "col-start-3 md:col-start-5 xl:col-start-7")}
        >
          {!hiddenButtons.includes("options") && (
            <DriveRowMenu
              variant="empty"
              item={item}
              isFolder={item.type === "folder"}
              currentFolderId={currentFolderId}
              triggerAriaLabel={`Actions for ${item.name}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </DriveRowMenu>
          )}
        </div>
      </div>
      {item.type === "file" && isActionable && expanded && (
        <div role="row">
          <div role="cell">
            <DriveRowSummary item={item} />
          </div>
        </div>
      )}
    </>
  );
};
